import React, { Component } from 'react';
import axios from 'axios';
import '../static/css/SignUpInfo.css';
import { State, City } from '../usLocations';
import { readAffiliateRef, clearAffiliateRef } from '../affiliateRef';
import { storageAvailable, STORAGE_BLOCKED_MESSAGE } from '../safeStorage';
import logo from '../static/images/images/a-logo-transperent.png';
import { Link } from 'react-router-dom';
import heic2any from 'heic2any';

// 🔒 Shared verbatim with iOS and Android this round. Never name the category, never say "nudity".
const PHOTO_REJECTED_TITLE = 'Photo Not Accepted';
const PHOTO_REJECTED_MESSAGE = "This photo can't be used. Nudity and sexual content aren't allowed. Please choose a different one.";

// The API is unversioned, so a-server can start returning an error key this file has never heard of.
// Before this existed, an unrecognised key collapsed into "Something went wrong. Please try again."
// and the real reason was silently dropped. Returns '' when there is nothing worth showing.
const genericServerError = (data) => {
    if (!data || typeof data !== 'object') return '';
    const usable = (value) => {
        if (typeof value !== 'string') return '';
        const trimmed = value.trim();
        // Guard against a proxy's HTML error page or a novel-length body landing in the UI.
        if (!trimmed || trimmed === 'SUCCESS' || trimmed.charAt(0) === '<' || trimmed.length > 300) return '';
        return trimmed;
    };
    // The two deliberate generic channels first (server.js's global handler sends {error}).
    const named = usable(data.error) || usable(data.message);
    if (named) return named;
    // Then ANY keyed string in the body. a-server's failures are flat key -> English-sentence
    // objects, so a key this file has never heard of still carries something the user can act on.
    // Bare-string 500 bodies ("Server Error!" from the route catch-alls, and validation.js's
    // "Something went wrong, please try agian later.") are developer strings — they fail the
    // typeof check above and are deliberately never shown.
    const keys = Object.keys(data);
    for (let i = 0; i < keys.length; i++) {
        const value = usable(data[keys[i]]);
        if (value) return value;
    }
    return '';
};

class CommunitySignUp extends Component {
    constructor(props) {
        super(props);
        this.state = {
            isLoading: false,
            first_name: '',
            last_name: '',
            email: '',
            password: '',
            confirm_password: '',
            display_name: '',
            city: '',
            states: '',
            first_name_required: '',
            first_name_min: '',
            first_name_max: '',
            first_name_valid: '',
            last_name_required: '',
            last_name_min: '',
            last_name_max: '',
            last_name_valid: '',
            email_required: '',
            email_valid: '',
            email_exists_error: '',
            email_direct: '',
            email_direct_link: null,
            password_required: '',
            password_min: '',
            password_special_character: '',
            password_number: '',
            confirm_password_required: '',
            confirm_password_match: '',
            display_name_required: '',
            display_name_min: '',
            display_name_max: '',
            display_name_error: '',
            display_name_exists_error: '',
            display_name_max_error: '',
            state_city_error: '',
            redirect: false,
            email_exists_gmail: null,
            usStates: State.getStatesOfCountry('US'),
            usStateCities: '',
            birth_month: 'Select Birth Month',
            birth_day: 'Select Birth Day',
            birth_year: 'Select Birth Year',
            birthdate_error: '',
            birthdate: '',
            gender: '',
            gender_error: '',
            image: null,
            image_preview: null,
            image_error: '',
            general_error: '',
            // Probed once here rather than per render — see SignUp.js.
            storageBlocked: !storageAvailable(),
            showRejectionModal: false,
            rejectionMessage: '',
            // Bumped to remount the <input type="file"> after a server-rejected photo, so the native
            // input is genuinely empty and re-picking the SAME file still fires onChange.
            image_input_key: 0,
        };

        this.onChangeFirstName = this.onChangeFirstName.bind(this);
        this.onChangeLastName = this.onChangeLastName.bind(this);
        this.onChangeEmail = this.onChangeEmail.bind(this);
        this.onChangePassword = this.onChangePassword.bind(this);
        this.onChangeConfirmPassword = this.onChangeConfirmPassword.bind(this);
        this.onChangeCity = this.onChangeCity.bind(this);
        this.onChangeStates = this.onChangeStates.bind(this);
        this.onChangeDisplayName = this.onChangeDisplayName.bind(this);
        this.onChangeBirthMonth = this.onChangeBirthMonth.bind(this);
        this.onChangeBirthDay = this.onChangeBirthDay.bind(this);
        this.onChangeBirthYear = this.onChangeBirthYear.bind(this);
        this.onChangeGender = this.onChangeGender.bind(this);
        this.onChangeImage = this.onChangeImage.bind(this);
        this.onSubmit = this.onSubmit.bind(this);
        this.closeRejectionModal = this.closeRejectionModal.bind(this);
    }

    // ── Change Handlers ──

    onChangeFirstName(event) { this.setState({ first_name: event.target.value }); }
    onChangeLastName(event) { this.setState({ last_name: event.target.value }); }
    onChangeEmail(event) { this.setState({ email: event.target.value }); }
    onChangePassword(event) { this.setState({ password: event.target.value }); }
    onChangeConfirmPassword(event) { this.setState({ confirm_password: event.target.value }); }
    onChangeDisplayName(event) { this.setState({ display_name: event.target.value }); }
    onChangeGender(event) { this.setState({ gender: event.target.value, gender_error: '' }); }
    async onChangeImage(event) {
        const file = event.target.files[0];
        if (file) {
            const isHeic = file.type === 'image/heic' || file.name.toLowerCase().endsWith('.heic');
            if (isHeic) {
                this.setState({ image: file, image_preview: 'converting', image_error: '' });
                try {
                    const blob = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.8 });
                    // heic2any takes seconds. A server rejection (or another pick) may have replaced
                    // or cleared the file while it ran — don't re-show a preview for a dropped file.
                    if (this.state.image === file) this.setState({ image_preview: URL.createObjectURL(blob) });
                } catch {
                    if (this.state.image === file) this.setState({ image_preview: 'unsupported' });
                }
            } else {
                this.setState({ image: file, image_preview: URL.createObjectURL(file), image_error: '' });
            }
        }
    }

    onChangeCity(event) { this.setState({ city: event.target.value }); }

    onChangeStates(event) {
        this.setState({
            states: event.target.value,
            usStateCities: City.getCitiesOfState('US', event.target.value),
        });
        if (this.state.states === "Select State") {
            this.setState({ city: "Select City" });
        }
    }

    // ── Validators ──

    validLetters = (val) => /^[A-Za-z_ ]+$/.test(val);
    validEmail = (email) => /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/.test(email);
    validPasswordNumber = (password) => /\d/.test(password);
    validPasswordSpecialCharacter = (password) => /^(?=.*[^a-zA-Z0-9])[\x21-\x7E]+$/.test(password);
    validAlphaNumeric = (val) => /^[a-zA-Z0-9._]*$/.test(val);

    // ── Birthdate Helpers ──

    months = [
        { label: 'Jan', value: '01' }, { label: 'Feb', value: '02' },
        { label: 'Mar', value: '03' }, { label: 'Apr', value: '04' },
        { label: 'May', value: '05' }, { label: 'Jun', value: '06' },
        { label: 'Jul', value: '07' }, { label: 'Aug', value: '08' },
        { label: 'Sep', value: '09' }, { label: 'Oct', value: '10' },
        { label: 'Nov', value: '11' }, { label: 'Dec', value: '12' },
    ];

    daysInMonth = (year, monthValue) => {
        const y = parseInt(year, 10);
        const m = parseInt(monthValue, 10);
        if (!y || !m) return 31;
        return new Date(y, m, 0).getDate();
    };

    pad2 = (n) => String(n).padStart(2, '0');

    buildBirthdateString = (year, monthValue, day) => {
        if (!year || !monthValue || !day) return "";
        return `${String(year)}-${String(monthValue)}-${this.pad2(day)}`;
    };

    is18OrOlder = (yyyy, mm, dd) => {
        const year = parseInt(yyyy, 10);
        const month = parseInt(mm, 10);
        const day = parseInt(dd, 10);
        if (!year || !month || !day) return false;

        const birth = new Date(year, month - 1, day);
        if (birth.getFullYear() !== year || birth.getMonth() !== (month - 1) || birth.getDate() !== day) {
            return false;
        }

        const now = new Date();
        const cutoff = new Date(now.getFullYear() - 18, now.getMonth(), now.getDate());
        return birth <= cutoff;
    };

    onChangeBirthMonth(event) {
        const monthValue = event.target.value;
        this.setState((prev) => {
            const maxDay = prev.birth_year !== 'Select Birth Year' && monthValue !== 'Select Birth Month'
                ? this.daysInMonth(prev.birth_year, monthValue) : 31;
            const currentDayInt = parseInt(prev.birth_day, 10);
            const shouldResetDay = prev.birth_day !== 'Select Birth Day' && (!currentDayInt || currentDayInt > maxDay);
            const nextDay = shouldResetDay ? 'Select Birth Day' : prev.birth_day;
            const nextBirthdateString = prev.birth_year !== 'Select Birth Year' && monthValue !== 'Select Birth Month' && nextDay !== 'Select Birth Day'
                ? this.buildBirthdateString(prev.birth_year, monthValue, nextDay) : "";
            return { birth_month: monthValue, birth_day: nextDay, birthdate: nextBirthdateString, birthdate_error: '' };
        });
    }

    onChangeBirthDay(event) {
        const dayValue = event.target.value;
        this.setState((prev) => {
            const nextBirthdateString = prev.birth_year !== 'Select Birth Year' && prev.birth_month !== 'Select Birth Month' && dayValue !== 'Select Birth Day'
                ? this.buildBirthdateString(prev.birth_year, prev.birth_month, dayValue) : "";
            return { birth_day: dayValue, birthdate: nextBirthdateString, birthdate_error: '' };
        });
    }

    onChangeBirthYear(event) {
        const yearValue = event.target.value;
        this.setState((prev) => {
            const maxDay = yearValue !== 'Select Birth Year' && prev.birth_month !== 'Select Birth Month'
                ? this.daysInMonth(yearValue, prev.birth_month) : 31;
            const currentDayInt = parseInt(prev.birth_day, 10);
            const shouldResetDay = prev.birth_day !== 'Select Birth Day' && (!currentDayInt || currentDayInt > maxDay);
            const nextDay = shouldResetDay ? 'Select Birth Day' : prev.birth_day;
            const nextBirthdateString = yearValue !== 'Select Birth Year' && prev.birth_month !== 'Select Birth Month' && nextDay !== 'Select Birth Day'
                ? this.buildBirthdateString(yearValue, prev.birth_month, nextDay) : "";
            return { birth_year: yearValue, birth_day: nextDay, birthdate: nextBirthdateString, birthdate_error: '' };
        });
    }

    birth_month_options() {
        return this.months.map(m => (
            <option value={m.value} key={m.value}>{m.label}</option>
        ));
    }

    birth_year_options() {
        const nowYear = new Date().getFullYear();
        const maxYear = nowYear - 18;
        const start = nowYear - 100;
        const years = [];
        for (let y = maxYear; y >= start; y--) years.push(y);
        return years.map(y => (
            <option value={String(y)} key={String(y)}>{String(y)}</option>
        ));
    }

    birth_day_options() {
        const { birth_year, birth_month } = this.state;
        let max = 31;
        if (birth_year !== 'Select Birth Year' && birth_month !== 'Select Birth Month') {
            max = this.daysInMonth(birth_year, birth_month);
        }
        const days = [];
        for (let d = 1; d <= max; d++) days.push(d);
        return days.map(d => (
            <option value={String(d)} key={String(d)}>{String(d)}</option>
        ));
    }

    // ── Dropdown Helpers ──

    us_states() {
        return this.state.usStates.map(state => (
            <option key={state.isoCode}>{state.isoCode}</option>
        ));
    }

    us_states_cities() {
        return this.state.usStateCities.map(city => (
            <option style={{ textAlign: "center" }} key={city.name}>{city.name}</option>
        ));
    }

    // ── Submit ──

    async onSubmit(event) {
        event.preventDefault();

        // 🔴 STOP BEFORE THE ACCOUNT EXISTS. With storage blocked the JWT cannot be persisted,
        // so a completed signup leaves them with an account they cannot reach AND AN EMAIL ADDRESS
        // THAT IS NOW TAKEN — retrying after fixing their browser returns email_exists_error.
        // Checked before validation and before any network call, so no POST is issued.
        if (this.state.storageBlocked) return;

        // A server message from a previous attempt must not survive above fresh client-side errors.
        // The catch below is the only other writer, so clearing here is the whole lifecycle.
        this.setState({ general_error: '' });

        let hasError = false;

        // First name
        if (!this.state.first_name || this.state.first_name.length < 1) {
            this.setState({ first_name_required: "FIRST NAME required!" }); hasError = true;
        } else { this.setState({ first_name_required: '' }); }

        if (this.state.first_name.length > 0 && this.state.first_name.length < 2) {
            this.setState({ first_name_min: "FIRST NAME must contain a minimum of 2 characters!" }); hasError = true;
        } else { this.setState({ first_name_min: '' }); }

        if (this.state.first_name.length > 45) {
            this.setState({ first_name_max: "FIRST NAME cannot contain more than 45 characters!" }); hasError = true;
        } else { this.setState({ first_name_max: '' }); }

        if (this.state.first_name.length > 0 && !this.validLetters(this.state.first_name)) {
            this.setState({ first_name_valid: "FIRST NAME must contain letters only!" }); hasError = true;
        } else { this.setState({ first_name_valid: '' }); }

        // Last name
        if (!this.state.last_name || this.state.last_name.length < 1) {
            this.setState({ last_name_required: "LAST NAME required!" }); hasError = true;
        } else { this.setState({ last_name_required: '' }); }

        if (this.state.last_name.length > 0 && this.state.last_name.length < 2) {
            this.setState({ last_name_min: "LAST NAME must contain a minimum of 2 characters!" }); hasError = true;
        } else { this.setState({ last_name_min: '' }); }

        if (this.state.last_name.length > 45) {
            this.setState({ last_name_max: "LAST NAME cannot contain more than 45 characters!" }); hasError = true;
        } else { this.setState({ last_name_max: '' }); }

        if (this.state.last_name.length > 0 && !this.validLetters(this.state.last_name)) {
            this.setState({ last_name_valid: "LAST NAME must contain letters only!" }); hasError = true;
        } else { this.setState({ last_name_valid: '' }); }

        // Email
        if (this.state.email.length === 0) {
            this.setState({ email_required: "EMAIL required!" }); hasError = true;
        } else { this.setState({ email_required: '' }); }

        if (this.state.email.length > 0 && !this.validEmail(this.state.email)) {
            this.setState({ email_valid: "Invalid EMAIL input!" }); hasError = true;
        } else { this.setState({ email_valid: '' }); }

        // Password
        if (!this.state.password || this.state.password.length < 1) {
            this.setState({ password_required: "PASSWORD required!" }); hasError = true;
        } else { this.setState({ password_required: "" }); }

        if ((this.state.password.length > 0 && this.state.password.length <= 7) || (this.state.password.length > 0 && this.state.password.length > 64)) {
            this.setState({ password_min: "PASSWORD can only be between 8-64 characters!" }); hasError = true;
        } else { this.setState({ password_min: "" }); }

        if (this.state.password.length >= 8 && !this.validPasswordSpecialCharacter(this.state.password)) {
            this.setState({ password_special_character: "PASSWORD must contain at least one special character! (any symbol except spaces)" }); hasError = true;
        } else { this.setState({ password_special_character: "" }); }

        if (this.state.password.length >= 8 && !this.validPasswordNumber(this.state.password)) {
            this.setState({ password_number: "PASSWORD must contain at least one number!" }); hasError = true;
        } else { this.setState({ password_number: "" }); }

        // Confirm password
        if (!this.state.confirm_password || this.state.confirm_password.length < 1) {
            this.setState({ confirm_password_required: "CONFIRM PASSWORD required!" }); hasError = true;
        } else { this.setState({ confirm_password_required: "" }); }

        if (this.state.confirm_password && this.state.password.localeCompare(this.state.confirm_password) === 1) {
            this.setState({ confirm_password_match: "PASSWORDS don't match!" }); hasError = true;
        } else { this.setState({ confirm_password_match: "" }); }

        // Display name
        // Minimum 2 because /people/search ignores queries shorter than 2 characters, which makes a
        // 1-character name unfindable. Mirrors a-server/utils/displayNameRules.js.
        if (!this.state.display_name || this.state.display_name.length < 2) {
            this.setState({ display_name_required: "DISPLAY NAME must be at least 2 characters!" }); hasError = true;
        } else { this.setState({ display_name_required: '' }); }

        if (this.state.display_name && this.state.display_name.length > 25) {
            this.setState({ display_name_max_error: "DISPLAY NAME cannot contain more than 25 characters!" }); hasError = true;
        } else { this.setState({ display_name_max_error: "" }); }

        if (!this.validAlphaNumeric(this.state.display_name)) {
            this.setState({ display_name_error: "DISPLAY NAME can only contain letters, numbers, underscores, and periods with no spaces." }); hasError = true;
        } else { this.setState({ display_name_error: "" }); }

        // State & City
        if (!this.state.states || this.state.states === 'Select State' || !this.state.city || this.state.city === 'Select City') {
            this.setState({ state_city_error: "You must select STATE and CITY!" }); hasError = true;
        } else { this.setState({ state_city_error: "" }); }

        // Birthdate
        let birthdateString = '';
        if (this.state.birth_month === 'Select Birth Month' || this.state.birth_day === 'Select Birth Day' || this.state.birth_year === 'Select Birth Year') {
            this.setState({ birthdate_error: "BIRTHDATE required!" }); hasError = true;
        } else {
            const yyyy = this.state.birth_year;
            const mm = this.state.birth_month;
            const dd = this.pad2(this.state.birth_day);
            birthdateString = this.buildBirthdateString(yyyy, mm, dd);

            if (!birthdateString) {
                this.setState({ birthdate_error: "Invalid BIRTHDATE selection!" }); hasError = true;
            } else if (!this.is18OrOlder(yyyy, mm, dd)) {
                this.setState({ birthdate_error: "You must be 18 years or older to continue!" }); hasError = true;
            } else {
                this.setState({ birthdate_error: "", birthdate: birthdateString });
            }
        }

        // Gender
        if (!this.state.gender || this.state.gender === 'Select Gender') {
            this.setState({ gender_error: "GENDER required!" }); hasError = true;
        } else { this.setState({ gender_error: "" }); }

        // Image
        if (!this.state.image) {
            this.setState({ image_error: "PROFILE PHOTO required!" }); hasError = true;
        } else { this.setState({ image_error: "" }); }

        // Stop if any errors
        if (hasError) return;

        // Build FormData payload for file upload
        const formData = new FormData();
        formData.append('first_name', this.state.first_name);
        formData.append('last_name', this.state.last_name);
        formData.append('email', this.state.email);
        formData.append('password', this.state.password);
        formData.append('confirm_password', this.state.confirm_password);
        formData.append('city', this.state.city);
        formData.append('states', this.state.states);
        formData.append('display_name', this.state.display_name);
        formData.append('birthdate', birthdateString);
        formData.append('gender', this.state.gender);
        formData.append('image', this.state.image);

        // Attach captured affiliate ref (set by AffiliateLanding when the visitor came in via /<displayName>).
        // readAffiliateRef() enforces the 365-day window and clears an expired or malformed value;
        // null means no ref goes on the request. It never throws, so no try/catch is needed here.
        const affiliateRef = readAffiliateRef();
        if (affiliateRef) formData.append('ref', affiliateRef);

        this.setState({ isLoading: true });

        await axios.post(process.env.REACT_APP_SERVER_API + '/auth/pre_signup_community_profile', formData, { headers: { 'Content-Type': 'multipart/form-data' } })
            .then(response => {
                if (response.data.message === "SUCCESS") {
                    localStorage.setItem('prelaunch_token', response.data.token);
                    clearAffiliateRef();
                    window.location = "/signup-confirmation-community";
                }
            })
            .catch(err => {
                const data = err.response && err.response.data ? err.response.data : {};
                // Photo moderation (a-server returns 400 {image_rejected_error}). Prefer the server's
                // wording, fall back to the shared copy when absent — same rule as iOS and Android.
                const photoRejected = typeof data.image_rejected_error !== 'undefined';
                const rejectionMessage = photoRejected
                    ? (genericServerError({ error: data.image_rejected_error }) || PHOTO_REJECTED_MESSAGE)
                    : '';
                const mapped = {
                    first_name_required: data.first_name_required || '',
                    first_name_min: data.first_name_min || '',
                    first_name_max: data.first_name_max || '',
                    first_name_valid: data.first_name_valid || '',
                    last_name_required: data.last_name_required || '',
                    last_name_min: data.last_name_min || '',
                    last_name_max: data.last_name_max || '',
                    last_name_valid: data.last_name_valid || '',
                    password_required: data.password_required || '',
                    password_min: data.password_min || '',
                    password_special_character: data.password_special_character || '',
                    password_number: data.password_number || '',
                    confirm_password_required: data.confirm_password_required || '',
                    confirm_password_match: data.confirm_password_match || '',
                    state_city_error: data.states_error || '',
                    email_exists: data.email_exists_error || '',
                    display_name_error: data.display_name_error || '',
                    display_name_max_error: data.display_name_max_error || '',
                    display_name_exists_error: data.display_name_exists_error || '',
                    image_error: rejectionMessage,
                };
                // Nothing above matched? The body is a key this component has never heard of. The API
                // is unversioned, so that will keep happening — say something useful instead of nothing.
                const recognised = Object.keys(mapped).some(k => mapped[k]);
                this.setState(prev => ({
                    ...mapped,
                    general_error: recognised ? '' : (genericServerError(data) || 'Something went wrong. Please try again.'),
                    showRejectionModal: photoRejected,
                    rejectionMessage: photoRejected ? rejectionMessage : prev.rejectionMessage,
                    // Drop the rejected file so the form cannot resubmit it, and remount the file
                    // input so the user is genuinely prompted to choose another.
                    image: photoRejected ? null : prev.image,
                    image_preview: photoRejected ? null : prev.image_preview,
                    image_input_key: photoRejected ? prev.image_input_key + 1 : prev.image_input_key,
                    isLoading: false,
                }));
            });
    }

    closeRejectionModal() {
        // Only the pop-up closes — the inline error above the photo box stays, so the reason survives.
        this.setState({ showRejectionModal: false });
    }

    // Hand-rolled, like every other modal in a-client. Own prefix (signup-info-modal-*) because CRA
    // bundles every stylesheet into one global sheet and SignUp.css already owns .signup-modal-*.
    renderRejectionModal() {
        return (
            <div
                className="signup-info-modal-overlay"
                role="dialog"
                aria-modal="true"
                aria-labelledby="signup-info-modal-title"
            >
                <div className="signup-info-modal-card">
                    <h3 id="signup-info-modal-title" className="signup-info-modal-title">{PHOTO_REJECTED_TITLE}</h3>
                    <p className="signup-info-modal-message">{this.state.rejectionMessage || PHOTO_REJECTED_MESSAGE}</p>
                    <div className="signup-info-modal-actions">
                        <button
                            type="button"
                            className="signup-info-modal-dismiss"
                            onClick={this.closeRejectionModal}
                            autoFocus
                        >
                            Got it
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // ── Render ──

    render() {
        let email_exists = this.state.email_exists
            ? <p className="signup-form-error-message">{this.state.email_exists}</p>
            : null;

        let states = this.us_states();
        let cities = this.state.usStateCities === '' ? <option disabled>Select State First</option> : this.us_states_cities();

        const body = <div>
                <div className="animates-logo-container"><Link to={'/coming-soon'}><img className="animates-logo hover" src={logo} alt="" /></Link></div>
                <div className="signup-info-box">
                    <h3 className="signup-info-title" style={{ marginBottom: "50px" }}><u>Profile Details</u></h3>
                    <form onSubmit={this.onSubmit}>
                        <p className="signup-message"><u>ATTENTION:</u> Once your profile is created, you cannot change your FIRST NAME, LAST NAME or DATE OF BIRTH!. You must be 18 years of age or older to create an account. LAST NAME is only private to us and will not be shared with the public.</p><br /><br />

                        {this.state.storageBlocked && <p className="signup-form-error-message">{STORAGE_BLOCKED_MESSAGE}</p>}
                        {this.state.general_error && <p className="signup-form-error-message">{this.state.general_error}</p>}
                        {this.state.image_error && <p className="signup-form-error-message">{this.state.image_error}</p>}
                        <p style={{ marginTop: "15px", marginBottom: "0px", textAlign: "center", fontSize: "14px" }}>Profile Photo</p>
                        <p style={{ textAlign: "center", color: "black", marginTop: "2px", marginBottom: "5px" }}>This can be a photo of you, your favorite anime character, a GIF, or anything that represents you!</p>
                        <div style={{ width: "100px", height: "100px", borderRadius: "50%", backgroundColor: "#e0e0e0", margin: "10px auto", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
                            {this.state.image_preview && this.state.image_preview !== 'unsupported' && this.state.image_preview !== 'converting'
                                ? <img src={this.state.image_preview} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                : this.state.image_preview === 'converting'
                                    ? <div style={{ width: "30px", height: "30px", border: "3px solid #e0e0e0", borderTop: "3px solid #85C7DD", borderRadius: "50%", animation: "spin 1s linear infinite" }}></div>
                                    : this.state.image_preview === 'unsupported'
                                        ? <span style={{ fontSize: "10px", color: "#999", textAlign: "center", padding: "5px" }}>Preview not available</span>
                                        : <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#999" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"></path><circle cx="12" cy="13" r="4"></circle></svg>
                            }
                        </div>
                        <p style={{ textAlign: "center", marginBottom: "5px", color: "#999" }}>{this.state.image ? this.state.image.name : "No file chosen"}</p>
                        <label style={{ display: "block", textAlign: "center", cursor: "pointer" }}>
                            <span style={{ backgroundColor: "white", color: "#85C7DD", border: "1px solid #85C7DD", padding: "5px 15px", borderRadius: "5px", fontSize: "11px", marginTop: "10px", display: "inline-block" }}>Choose File</span>
                            <input key={this.state.image_input_key} type="file" accept="image/*" onChange={this.onChangeImage} style={{ display: "none" }} />
                        </label><br />

                        <p className="signup-form-error-message">{this.state.first_name_required}</p>
                        <p className="signup-form-error-message">{this.state.first_name_min}</p>
                        <p className="signup-form-error-message">{this.state.first_name_max}</p>
                        <p className="signup-form-error-message">{this.state.first_name_valid}</p>
                        <input className="form-input" type="text" placeholder="First Name" value={this.state.first_name} onChange={this.onChangeFirstName} />

                        <p className="signup-form-error-message">{this.state.last_name_required}</p>
                        <p className="signup-form-error-message">{this.state.last_name_min}</p>
                        <p className="signup-form-error-message">{this.state.last_name_max}</p>
                        <p className="signup-form-error-message">{this.state.last_name_valid}</p>
                        <input className="form-input" type="text" placeholder="Last Name" value={this.state.last_name} onChange={this.onChangeLastName} />

                        <p className="signup-form-error-message">{this.state.display_name_required}</p>
                        <p className="signup-form-error-message">{this.state.display_name_error}</p>
                        <p className="signup-form-error-message">{this.state.display_name_max_error}</p>
                        <p className="signup-form-error-message">{this.state.display_name_exists_error}</p>
                        <input className="form-input" type="text" placeholder="Display Name" value={this.state.display_name} onChange={this.onChangeDisplayName} />

                        <p className="signup-form-error-message">{this.state.email_required}</p>
                        <p className="signup-form-error-message">{this.state.email_valid}</p>
                        {email_exists}
                        <input className="form-input" type="text" placeholder="Email" value={this.state.email} onChange={this.onChangeEmail} />

                        <p className="signup-form-error-message">{this.state.password_required}</p>
                        <p className="signup-form-error-message">{this.state.password_min}</p>
                        <p className="signup-form-error-message">{this.state.password_number}</p>
                        <p className="signup-form-error-message">{this.state.password_special_character}</p>
                        <input className="form-input" type="password" placeholder="Password" value={this.state.password} onChange={this.onChangePassword} />

                        <p className="signup-form-error-message">{this.state.confirm_password_required}</p>
                        <p className="signup-form-error-message">{this.state.confirm_password_match}</p>
                        <input className="form-input" type="password" placeholder="Confirm Password" value={this.state.confirm_password} onChange={this.onChangeConfirmPassword} />

                        <p className="signup-form-error-message">{this.state.state_city_error}</p>
                        <select className="form-input" value={this.state.states} onChange={this.onChangeStates}>
                            <option>Select State</option>
                            {states}
                        </select>

                        <select className="form-input" value={this.state.city} onChange={this.onChangeCity}>
                            <option>Select City</option>
                            {cities}
                        </select>

                        <p className="signup-form-error-message">{this.state.birthdate_error}</p>
                        <div style={{ display: "flex", gap: "10px", width: "85%", margin: "0 auto" }}>
                            <select className="form-input" value={this.state.birth_month} onChange={this.onChangeBirthMonth} style={{ textAlign: "center", marginTop: "5px" }}>
                                <option>Select Birth Month</option>
                                {this.birth_month_options()}
                            </select>
                            <select className="form-input" value={this.state.birth_day} onChange={this.onChangeBirthDay} style={{ textAlign: "center", marginTop: "5px" }} disabled={this.state.birth_month === "Select Birth Month"}>
                                <option>Select Birth Day</option>
                                {this.birth_day_options()}
                            </select>
                            <select className="form-input" value={this.state.birth_year} onChange={this.onChangeBirthYear} style={{ textAlign: "center", marginTop: "5px" }}>
                                <option>Select Birth Year</option>
                                {this.birth_year_options()}
                            </select>
                        </div>

                        {this.state.gender_error && <p className="signup-form-error-message">{this.state.gender_error}</p>}
                        <select className="form-input" style={{ marginTop: "0px" }} value={this.state.gender} onChange={this.onChangeGender}>
                            <option value="">Select Gender</option>
                            <option value="Man">Man</option>
                            <option value="Woman">Woman</option>
                            <option value="Nonbinary">Nonbinary</option>
                            <option value="Prefer not to say">Prefer not to say</option>
                        </select>

                        <button className="form-button" disabled={this.state.isLoading}>{this.state.isLoading ? 'Loading...' : 'Create'}</button>
                    </form>
                </div>
                <p className="signup-info-footer">© 2026 AniMates. All rights reserved.</p>
            </div>;

        return (
            <div className="signup-info-container" style={{ fontSize: "10px" }}>
                {body}
                {this.state.showRejectionModal && this.renderRejectionModal()}
            </div>
        );
    }
}

export default CommunitySignUp;
